<script setup lang="ts">
import { Head, usePage } from "@inertiajs/vue3";
import { computed, onMounted, onUnmounted, ref } from "vue";

import { Badge } from "@/components/ui/badge/index";
import { Button } from "@/components/ui/button/index";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card/index";
import { Switch } from "@/components/ui/switch/index";
import { useCsrfToken } from "@/composables/use-csrf-token";
import SettingsLayout from "@/layouts/settings/layout";
import {
  type PwaDiagnosticsPayload,
  SW_SCOPE,
  SW_URL,
  buildPwaDiagnosticsPayload,
  postPwaDiagnostics,
} from "@/lib/pwa";
import {
  formatPwaRegistrationState,
  readPwaRegistrationSnapshot,
  waitForUsablePwaRegistration,
} from "@/lib/pwa-registration";
import {
  organizationPwaDemoPath,
  organizationPwaSubscriptionPath,
} from "@/routes";

defineOptions({ inheritAttrs: false });
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const page = usePage<{
  auth?: { organization?: { id: number } };
  pwa?: { vapid_public_key?: string };
}>();

const organizationId = computed(() => page.props.auth?.organization?.id);
const vapidPublicKey = computed(() => page.props.pwa?.vapid_public_key);

const installed = ref(false);
const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null);
const swRegistered = ref<"checking" | "yes" | "no">("checking");
const swControlling = ref<"yes" | "no">("no");
const registrationState = ref("missing");
const permission = ref<NotificationPermission>("default");
const subscribed = ref(false);
const busy = ref(false);
const error = ref<string | null>(null);
const lastMessage = ref<string | null>(null);
const inAppPreview = ref(true);
const debugEntries = ref<string[]>([]);

const isDev = import.meta.env.MODE !== "production";
const csrfToken = useCsrfToken();

const pushSupported = computed(
  () =>
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    typeof Notification !== "undefined",
);

const isInstalled = () => {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  return (
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
};

const debugLog = (...args: unknown[]) => {
  if (!isDev) return;
  const message = args
    .map((value) => {
      if (typeof value === "string") return value;
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    })
    .join(" ");
  console.info("[PWA]", ...args);
  const next = [
    ...debugEntries.value,
    `${new Date().toLocaleTimeString()} ${message}`,
  ];
  debugEntries.value = next.slice(-40);
};

const syncStatusFromSnapshot = (
  snapshot: Awaited<ReturnType<typeof readPwaRegistrationSnapshot>>,
) => {
  swRegistered.value = snapshot.registration ? "yes" : "no";
  swControlling.value = snapshot.controllerPresent ? "yes" : "no";
  registrationState.value = formatPwaRegistrationState(snapshot);
};

const refreshServiceWorkerStatus = async () => {
  const snapshot = await readPwaRegistrationSnapshot();
  syncStatusFromSnapshot(snapshot);
  debugLog("Status check", {
    scope: SW_SCOPE,
    state: registrationState.value,
    hasRegistration: Boolean(snapshot.registration),
    hasController: snapshot.controllerPresent,
  });

  const existing = await snapshot.registration?.pushManager.getSubscription();
  subscribed.value = Boolean(existing);
};

const sendPwaDiagnostics = async (
  payload: Pick<PwaDiagnosticsPayload, "event" | "stage" | "message"> & {
    details?: Record<string, unknown>;
  },
) => {
  if (!organizationId.value) return;

  try {
    const snapshot = await readPwaRegistrationSnapshot();
    syncStatusFromSnapshot(snapshot);

    await postPwaDiagnostics({
      organizationId: organizationId.value,
      csrfToken: csrfToken.value,
      payload: buildPwaDiagnosticsPayload({
        ...payload,
        permission: permission.value,
        registration_state: formatPwaRegistrationState(snapshot),
        controller_present: snapshot.controllerPresent,
      }),
    });
  } catch (diagnosticsError) {
    debugLog("Diagnostics send failed", diagnosticsError);
  }
};

const withTimeout = async <T,>(
  promise: Promise<T>,
  ms: number,
  message: string,
) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  try {
    return await new Promise<T>((resolve, reject) => {
      timeoutId = setTimeout(() => reject(new Error(message)), ms);
      promise.then(resolve).catch(reject);
    });
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const urlBase64ToUint8Array = (value: string) => {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const handleInstall = async () => {
  if (!deferredPrompt.value) return;
  await deferredPrompt.value.prompt();
  const choice = await deferredPrompt.value.userChoice;
  if (choice.outcome === "accepted") {
    deferredPrompt.value = null;
  }
};

const handleEnablePush = async () => {
  if (!pushSupported.value || !organizationId.value) return;
  if (!vapidPublicKey.value) {
    error.value = "Missing VAPID public key.";
    void sendPwaDiagnostics({
      event: "push_subscription",
      stage: "invalid_config",
      message: "Missing VAPID public key.",
    });
    return;
  }

  busy.value = true;
  error.value = null;
  try {
    await refreshServiceWorkerStatus();
    debugLog("Enable push clicked", {
      swUrl: SW_URL,
      scope: SW_SCOPE,
      permission: permission.value,
    });
    debugLog("Environment", {
      origin: typeof window === "undefined" ? null : window.location.origin,
      secureContext:
        typeof window === "undefined" ? null : window.isSecureContext,
      baseUrl: import.meta.env.BASE_URL,
      controllerUrl: navigator.serviceWorker.controller?.scriptURL || null,
    });
    void sendPwaDiagnostics({
      event: "push_subscription",
      stage: "button_clicked",
      message: "User started push subscription flow.",
      details: {
        registration_state: registrationState.value,
      },
    });

    const nextPermission = await Notification.requestPermission();
    debugLog("Permission result", { nextPermission });
    permission.value = nextPermission;
    if (nextPermission !== "granted") {
      void sendPwaDiagnostics({
        event: "push_subscription",
        stage: "permission_not_granted",
        message: `Notification permission is ${nextPermission}.`,
      });
      return;
    }

    void sendPwaDiagnostics({
      event: "push_subscription",
      stage: "waiting_registration",
      message: "Waiting for usable service worker registration.",
    });

    const snapshot = await waitForUsablePwaRegistration({ timeoutMs: 10000 });
    syncStatusFromSnapshot(snapshot);
    const registration = snapshot.registration;
    if (!registration) {
      const message =
        "Service worker not ready. Reload the page and try again.";
      void sendPwaDiagnostics({
        event: "push_subscription",
        stage: "registration_missing",
        message,
      });
      throw new Error(message);
    }

    if ("permissionState" in registration.pushManager) {
      const state = await registration.pushManager.permissionState({
        userVisibleOnly: true,
      });
      debugLog("Push permissionState", { state });
    }

    const existingSubscription =
      await registration.pushManager.getSubscription();
    debugLog("Existing subscription", {
      hasSubscription: Boolean(existingSubscription),
    });
    void sendPwaDiagnostics({
      event: "push_subscription",
      stage: "subscription_lookup",
      message: existingSubscription
        ? "Found existing push subscription."
        : "No existing push subscription found.",
    });

    let subscription = existingSubscription;
    if (!subscription) {
      try {
        subscription = await withTimeout(
          registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey.value),
          }),
          15000,
          "Push subscription timed out.",
        );
      } catch (err) {
        debugLog("Subscribe failed or timed out", err);
        void sendPwaDiagnostics({
          event: "push_subscription",
          stage: "subscribe_timeout_retry",
          message:
            err instanceof Error
              ? err.message
              : "Push subscription timed out before completion.",
        });
        const retrySubscription =
          await registration.pushManager.getSubscription();
        debugLog("Subscription after timeout", {
          hasSubscription: Boolean(retrySubscription),
        });
        if (retrySubscription) {
          subscription = retrySubscription;
        } else {
          throw err;
        }
      }
    }
    debugLog("Subscription ready", { endpoint: subscription?.endpoint });

    // This endpoint persists push subscription via JSON, so we send a manual fetch request.
    const response = await fetch(
      organizationPwaSubscriptionPath(organizationId.value),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken.value,
        },
        credentials: "same-origin",
        body: JSON.stringify({ subscription: subscription?.toJSON() }),
      },
    );

    if (!response.ok) {
      throw new Error("Unable to save subscription.");
    }
    debugLog("Subscription saved");
    subscribed.value = true;
    void sendPwaDiagnostics({
      event: "push_subscription",
      stage: "subscription_saved",
      message: "Push subscription saved successfully.",
      details: {
        endpoint_present: Boolean(subscription?.endpoint),
      },
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unable to subscribe.";
    error.value = message;
    void sendPwaDiagnostics({
      event: "push_subscription",
      stage: "failed",
      message,
    });
  } finally {
    busy.value = false;
  }
};

const handleSendDemo = async () => {
  if (!organizationId.value) return;
  busy.value = true;
  error.value = null;
  try {
    // Demo push endpoint also returns JSON instead of an Inertia redirect/page payload.
    const response = await fetch(
      organizationPwaDemoPath(organizationId.value),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken.value,
        },
        credentials: "same-origin",
      },
    );
    if (!response.ok) {
      throw new Error("Server rejected demo push.");
    }
  } catch (err) {
    error.value =
      err instanceof Error ? err.message : "Unable to send notification.";
  } finally {
    busy.value = false;
  }
};

const handleLocalNotification = async () => {
  if (!pushSupported.value) return;
  busy.value = true;
  error.value = null;
  try {
    if (Notification.permission !== "granted") {
      const nextPermission = await Notification.requestPermission();
      permission.value = nextPermission;
      if (nextPermission !== "granted") {
        throw new Error("Permission not granted.");
      }
    }
    const snapshot = await waitForUsablePwaRegistration({ timeoutMs: 10000 });
    syncStatusFromSnapshot(snapshot);
    const registration = snapshot.registration;
    if (!registration) {
      throw new Error("Service worker not ready. Reload the page and try again.");
    }
    await registration.showNotification("Local test notification", {
      body: "This is a local test from the Service Worker.",
      icon: "/icon.png",
      badge: "/icon.png",
      data: { path: "/settings/pwa" },
    });
  } catch (err) {
    error.value =
      err instanceof Error ? err.message : "Unable to show notification.";
  } finally {
    busy.value = false;
  }
};

const copyDebugLogs = () => {
  void navigator.clipboard
    .writeText(debugEntries.value.join("\n"))
    .catch(() => undefined);
};

const handleTogglePreview = (value: boolean) => {
  inAppPreview.value = value;
  if (typeof window !== "undefined") {
    window.localStorage.setItem("pwa.in_app_preview", value ? "true" : "false");
  }
};

const installLabel = computed(() =>
  installed.value
    ? "App instalada"
    : deferredPrompt.value
      ? "Instalar app"
      : "Instalar (no disponible)",
);

// --- Lifecycle ---

const handleBeforeInstallPrompt = (event: Event) => {
  event.preventDefault();
  deferredPrompt.value = event as BeforeInstallPromptEvent;
};

const handleAppInstalled = () => {
  installed.value = true;
  deferredPrompt.value = null;
};

const handleControllerChange = () => {
  swControlling.value = navigator.serviceWorker.controller ? "yes" : "no";
  debugLog("Controller changed", {
    hasController: Boolean(navigator.serviceWorker.controller),
  });
  void refreshServiceWorkerStatus();
};

const handleSwMessage = (event: MessageEvent<unknown>) => {
  const data = event.data as
    | { type?: string; payload?: { title?: string } }
    | undefined;
  if (data?.type === "pwa.push") {
    const title = data.payload?.title || "Push received";
    lastMessage.value = title;
  }
};

onMounted(() => {
  installed.value = isInstalled();
  if (typeof Notification !== "undefined") {
    permission.value = Notification.permission;
  }

  const storedPreference = window.localStorage.getItem("pwa.in_app_preview");
  if (storedPreference !== null) {
    inAppPreview.value = storedPreference === "true";
  }

  void refreshServiceWorkerStatus().catch((statusError) => {
    debugLog("Status check failed", statusError);
    swRegistered.value = "no";
    registrationState.value = "missing";
    void sendPwaDiagnostics({
      event: "sw_registration",
      stage: "status_check_failed",
      message:
        statusError instanceof Error
          ? statusError.message
          : "Unable to read service worker status.",
    });
  });

  window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  window.addEventListener("appinstalled", handleAppInstalled);
  navigator.serviceWorker?.addEventListener(
    "controllerchange",
    handleControllerChange,
  );
  navigator.serviceWorker?.addEventListener("message", handleSwMessage);
});

onUnmounted(() => {
  window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  window.removeEventListener("appinstalled", handleAppInstalled);
  navigator.serviceWorker?.removeEventListener(
    "controllerchange",
    handleControllerChange,
  );
  navigator.serviceWorker?.removeEventListener("message", handleSwMessage);
});
</script>

<template>
  <Head title="PWA" />

  <main
    class="relative flex h-full min-h-0 w-full flex-col gap-3 bg-muted/50 p-3"
  >
    <SettingsLayout
      current="pwa"
      title="PWA"
      description="Instala la app, activa notificaciones y ajusta preferencias."
    >
      <div class="space-y-6">
        <!-- Installation card -->
        <Card>
          <CardHeader>
            <CardTitle>Instalación</CardTitle>
            <CardDescription>
              Añade Condor a tu escritorio para abrirla como app.
            </CardDescription>
          </CardHeader>
          <CardContent class="space-y-4">
            <div class="flex flex-wrap gap-2">
              <Badge variant="outline">
                <span
                  class="mr-1 inline-block size-2 rounded-full"
                  :class="installed ? 'bg-emerald-500' : 'bg-amber-500'"
                />
                {{ installed ? "Instalada" : "Navegador" }}
              </Badge>
              <Badge variant="outline">
                <span
                  class="mr-1 inline-block size-2 rounded-full"
                  :class="deferredPrompt ? 'bg-emerald-500' : 'bg-amber-500'"
                />
                {{ deferredPrompt ? "Instalable" : "No disponible" }}
              </Badge>
            </div>
            <div class="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                :disabled="installed || !deferredPrompt"
                @click="handleInstall"
              >
                {{ installLabel }}
              </Button>
            </div>
          </CardContent>
        </Card>

        <!-- Diagnostics card (dev only) -->
        <Card v-if="isDev">
          <CardHeader>
            <CardTitle>Diagnóstico</CardTitle>
            <CardDescription>
              Logs de desarrollo para depurar registro y push.
            </CardDescription>
          </CardHeader>
          <CardContent class="space-y-3">
            <div
              class="rounded-lg border border-border/60 bg-muted/40 p-3 text-xs text-muted-foreground"
            >
              <div>SW URL: {{ SW_URL || "N/A" }}</div>
              <div>Scope: {{ SW_SCOPE }}</div>
              <div>State: {{ registrationState }}</div>
              <div>Permission: {{ permission }}</div>
            </div>
            <div
              class="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-border/60 bg-background p-3 text-xs text-foreground/80"
            >
              <div v-if="debugEntries.length === 0">No logs yet.</div>
              <div v-for="(entry, index) in debugEntries" :key="index">
                {{ entry }}
              </div>
            </div>
            <div class="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                @click="debugEntries = []"
              >
                Limpiar logs
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                @click="copyDebugLogs"
              >
                Copiar logs
              </Button>
            </div>
          </CardContent>
        </Card>

        <!-- Push notifications card -->
        <Card>
          <CardHeader>
            <CardTitle>Notificaciones push</CardTitle>
            <CardDescription>
              Activa permisos y suscripción para recibir menciones y
              recordatorios.
            </CardDescription>
          </CardHeader>
          <CardContent class="space-y-4">
            <div class="flex flex-wrap gap-2">
              <Badge variant="outline">
                <span
                  class="mr-1 inline-block size-2 rounded-full"
                  :class="
                    swRegistered === 'yes' ? 'bg-emerald-500' : 'bg-red-500'
                  "
                />
                SW
                {{
                  swRegistered === "checking"
                    ? "Comprobando"
                    : swRegistered === "yes"
                      ? "Registrado"
                      : "No"
                }}
              </Badge>
              <Badge variant="outline">
                <span
                  class="mr-1 inline-block size-2 rounded-full"
                  :class="
                    swControlling === 'yes' ? 'bg-emerald-500' : 'bg-amber-500'
                  "
                />
                {{ swControlling === "yes" ? "Activo" : "Inactivo" }}
              </Badge>
              <Badge variant="outline">
                <span
                  class="mr-1 inline-block size-2 rounded-full"
                  :class="
                    permission === 'granted'
                      ? 'bg-emerald-500'
                      : permission === 'denied'
                        ? 'bg-red-500'
                        : 'bg-amber-500'
                  "
                />
                Permiso
                {{
                  permission === "granted"
                    ? "Concedido"
                    : permission === "denied"
                      ? "Denegado"
                      : "Pendiente"
                }}
              </Badge>
              <Badge variant="outline">
                <span
                  class="mr-1 inline-block size-2 rounded-full"
                  :class="subscribed ? 'bg-emerald-500' : 'bg-amber-500'"
                />
                {{ subscribed ? "Suscrito" : "Sin suscripción" }}
              </Badge>
            </div>

            <div class="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                :disabled="
                  !pushSupported ||
                  permission === 'denied' ||
                  busy ||
                  !organizationId
                "
                @click="handleEnablePush"
              >
                {{ subscribed ? "Push activo" : "Activar push" }}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                :disabled="!subscribed || busy || !organizationId"
                @click="handleSendDemo"
              >
                Enviar prueba
              </Button>
              <Button
                variant="secondary"
                size="sm"
                :disabled="busy || !pushSupported"
                @click="handleLocalNotification"
              >
                Probar local
              </Button>
            </div>

            <p v-if="error" class="text-xs text-red-500">{{ error }}</p>
          </CardContent>
        </Card>

        <!-- Preferences card -->
        <Card>
          <CardHeader>
            <CardTitle>Preferencias</CardTitle>
            <CardDescription>
              Estos ajustes se guardan en este navegador.
            </CardDescription>
          </CardHeader>
          <CardContent class="space-y-3">
            <div class="flex items-center justify-between gap-4">
              <div>
                <p class="text-sm font-medium text-foreground">
                  Previsualizar notificaciones en la app
                </p>
                <p class="text-xs text-muted-foreground">
                  Muestra un aviso interno cuando llega un push.
                </p>
              </div>
              <Switch
                :model-value="inAppPreview"
                @update:model-value="handleTogglePreview"
              />
            </div>

            <div
              v-if="lastMessage && inAppPreview"
              class="rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
            >
              Último push recibido:
              <span class="font-medium">{{ lastMessage }}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </SettingsLayout>
  </main>
</template>
